import { util, FabricObject, Canvas } from 'fabric';

/**
 * Safely enliven fabric objects with proper type checking
 * This utility prevents TypeScript errors when using fabric.util.enlivenObjects
 * 
 * @param objects Array of serialized fabric objects
 * @returns Promise of fabric objects that can be added to canvas
 */
export async function enlivenFabricObjects(objects: any[]): Promise<FabricObject[]> {
  const enlivened = await util.enlivenObjects(objects);
  
  // Filter out non-FabricObject items (like gradients, shadows, etc.)
  // Only return objects that have a 'type' property and can be added to canvas
  return enlivened.filter((obj: any): obj is FabricObject => {
    return obj && 'type' in obj && typeof obj.type === 'string';
  });
}

/**
 * Enliven and add objects to canvas
 * Handles the common pattern of enlivening objects and adding them to a canvas
 * 
 * @param canvas The fabric canvas instance
 * @param objects Array of serialized fabric objects
 * @returns Promise that resolves when all objects are added
 */
export async function enlivenAndAddToCanvas(
  canvas: Canvas, 
  objects: any[]
): Promise<void> {
  const fabricObjects = await enlivenFabricObjects(objects);
  
  fabricObjects.forEach(obj => {
    canvas.add(obj);
  });
  
  canvas.requestRenderAll();
}

/**
 * Get the name property of a fabric object safely
 * @param obj Fabric object
 * @returns The name property or undefined
 */
export function getObjectName(obj: any): string | undefined {
  return obj?.name || obj?.get?.('name');
}